import { cn } from '@Pages/pageRuntime/lib/utils';
import React from 'react';
type ListItem = {
  /**
   * Stable identifier for the row, used as the React key.
   */
  id?: string | number;

  /**
   * Row title.
   */
  title: React.ReactNode;

  /**
   * Optional supporting text below the title.
   */
  description?: React.ReactNode;

  /**
   * Optional metadata shown on the right (e.g. status, timestamp).
   */
  meta?: React.ReactNode;

  /**
   * Optional row action element.
   */
  action?: React.ReactNode;
};

type ListProps = {
  /**
   * List item definitions.
   */
  items: ListItem[];

  /**
   * Text shown when `items` is empty.
   */
  emptyText?: string;

  className?: string;
};

/**
 * A structured list for activity feeds, summaries, and lightweight record rows. Use when
 * rows need more layout than a table cell but less structure than cards.
 *
 * @example
 * <Sdk.Components.List items={tasks.map((task) => ({ id: task.id, title: task.name, meta: task.status }))} />
 */
export function List({ items, emptyText = 'No items found.', className }: ListProps) {
  if (items.length === 0) {
    return (
      <div
        className={cn(
          'rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground',
          className
        )}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <div
      className={cn('divide-y divide-border rounded-lg border border-border bg-card', className)}
    >
      {items.map((item, index) => (
        <div key={item.id ?? index} className="flex items-start justify-between gap-4 p-4">
          <div className="min-w-0">
            <div className="font-medium text-card-foreground">{item.title}</div>
            {item.description ? (
              <div className="mt-1 text-sm text-muted-foreground">{item.description}</div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {item.meta ? <div className="text-sm text-muted-foreground">{item.meta}</div> : null}
            {item.action ? <div>{item.action}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}
